import {
  collection,
  onSnapshot,
  query,
  where,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_STREET_SURVEY_KEY,
  isDefaultStreetSurvey,
  resolveStreetSurveyAiConfig,
  type District,
} from '@citizen-walk/shared-types';
import { db } from '../../lib/firebase';
import { toStreetSurveyPost, type StreetSurveyPost } from './streetSurveyPostProjection';
import { useStreetSurveyConfig } from './useStreetSurveyConfig';

export {
  toStreetSurveyPost,
  type StreetSurveyAnalysisStatus,
  type StreetSurveyPost,
} from './streetSurveyPostProjection';

/**
 * ストリートビュー机上調査の結果 post 一覧 (docs/25 Increment 2)。
 *
 * `usePostsModeration.ts` の onSnapshot パターンを踏襲し、
 * `extraFields.surveyMethod === 'streetview-desk'` の post だけを district 全体から購読する。
 * firestore.rules の `isDistrictAdmin` は既に `hidden_all` を含む全 visibility を admin に
 * 許可済みのため、追加のルール変更は不要 (usePostsModeration.ts と同じ read 経路)。
 *
 * 2026-07-14 修正: 解析結果 (classifications/description/professionalInsight) は
 * `post.aiAnalysis.walker` 固定キーではなく、`aiAnalysis.generic` (mode 非依存、
 * `streetSurveyGenericResult.ts` が M3 の fields[] から動的に解決) から読む。これにより
 * walker 以外のモード (bousai/kids-bousai/keikan 等) のテンプレートでもピンの色・
 * ドーナツ集計が正しく機能する (`usePrimaryAxisCategoryMaster.ts` と同じ「先頭 post の
 * settingsId で outputFormat を解決する」慣習に合わせる)。
 *
 * 2026-09-16 修正 (docs/98 R-98 高 1): 読み取りに使う出力フォーマットを、テンプレートの M3 だけでなく
 * 地区の上書き (`outputFormatIdOverride` / `outputFieldsOverride`) を反映した実効スキーマにする
 * (`useStreetSurveyConfig` と同じ解決。地区で分類の項目名を変えていると一覧が空になっていた)。
 */
export type StreetSurveyPostsState =
  | { status: 'loading'; posts: [] }
  | { status: 'ready'; posts: StreetSurveyPost[] }
  | { status: 'error'; posts: []; error: Error };

interface RawDoc {
  id: string;
  data: DocumentData;
}

type RawState =
  | { status: 'loading'; docs: [] }
  | { status: 'ready'; docs: RawDoc[] }
  | { status: 'error'; docs: []; error: Error };

export function useStreetSurveyPosts(
  districtId: string | null | undefined,
  /** 地区 (上書きの解決に使う)。読込中は null。 */
  district: District | null,
  /** 調査 (docs/98 R-97、未指定 = 既定の調査)。 */
  surveyKey: string = DEFAULT_STREET_SURVEY_KEY,
): StreetSurveyPostsState {
  const [rawState, setRawState] = useState<RawState>({ status: 'loading', docs: [] });

  useEffect(() => {
    if (!districtId) {
      setRawState({ status: 'error', docs: [], error: new Error('districtId is required') });
      return;
    }
    const q = query(
      collection(db, 'districts', districtId, 'posts'),
      where('extraFields.surveyMethod', '==', 'streetview-desk'),
    );
    const unsub: Unsubscribe = onSnapshot(
      q,
      (snap) => {
        const docs = snap.docs.map((d) => ({ id: d.id, data: d.data() }));
        setRawState({ status: 'ready', docs });
      },
      (error) => setRawState({ status: 'error', docs: [], error }),
    );
    return () => unsub();
  }, [districtId]);

  // 出力フォーマットの解決は「解析済みの post の settingsId」を代表値とし、無ければ地区の
  // AI 設定 (StreetSurveyPage.tsx と同じ慣習)。地区の上書きを反映した実効スキーマで読む。
  const additionalSettingsId = isDefaultStreetSurvey(surveyKey)
    ? null
    : (resolveStreetSurveyAiConfig(district?.aiAnalysis ?? null, surveyKey)?.settingsId ?? null);
  const primarySettingsId =
    additionalSettingsId ??
    (rawState.status === 'ready'
      ? rawState.docs
          .map(
            (d) =>
              (d.data.aiAnalysis as Record<string, unknown> | undefined)?.settingsId as
                | string
                | undefined,
          )
          .find((id) => typeof id === 'string' && id.length > 0)
      : undefined) ??
    district?.aiAnalysis?.settingsId ??
    null;
  const { schema, display } = useStreetSurveyConfig(district, primarySettingsId, surveyKey);
  const resultFields = display.result;

  const posts = useMemo(
    () =>
      rawState.status === 'ready'
        ? rawState.docs.map((d) =>
            toStreetSurveyPost(d.id, d.data, schema, resultFields, surveyKey),
          )
        : [],
    [rawState, schema, resultFields, surveyKey],
  );

  if (rawState.status === 'loading') return { status: 'loading', posts: [] };
  if (rawState.status === 'error') return { status: 'error', posts: [], error: rawState.error };
  return { status: 'ready', posts };
}
